import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardActions,
    Button,
    IconButton,
    Dialog,
    DialogContent,
    TextField,
    Switch,
    FormControlLabel,
    Grid,
    Chip,
    Alert,
    CircularProgress,
    Divider,
    Tooltip,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    OutlinedInput,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Snackbar,
    Collapse,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    Person as PersonIcon,
    LocalOffer as DiscountIcon,
    PlaylistAdd as SeedIcon,
    Palette as PaletteIcon,
    Label as LabelIcon,
    AttachMoney as PriceIcon,
    QueryStats as QuotaIcon,
    AutoAwesome as FeaturesIcon,
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    AdminPanelSettings as AdminIcon,
    Shield as ShieldIcon,
    VerifiedUser as VerifiedUserIcon,
    CheckCircle as CheckCircleIcon,
    DragIndicator as DragIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/api';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    rectSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ─── Accent palette ───────────────────────────────────────────────────────────
const ACCENT  = '#6366f1';
const GREEN   = '#10d98a';
const AMBER   = '#f59e0b';
const RED     = '#ef4444';

// ─── Default template for a new plan ──────────────────────────────────────────
const EMPTY_PLAN = {
    name: '',
    target: '',
    price: { usd: '$0', inr: '₹0' },
    period: '/mo',
    accent: '#6366f1',
    badge: '',
    outlined: true,
    active: true,
    quota: { usd: 100, inr: 50 },
    engagement_daily: { usd: 50, inr: 50 },
    priceIds: { usd: '', inr: '' },
    razorpayPlanId: '',
    razorpayAmountPaise: '',
    features_global: [],
    features_ind: [],
};

// ─── Section header ────────────────────────────────────────────────────────────
function SectionLabel({ icon, label, color = ACCENT }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{
                width: 28, height: 28, borderRadius: '8px',
                bgcolor: alpha(color, 0.15),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { fontSize: 15, color } })}
            </Box>
            <Typography variant="caption" sx={{
                fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.08em',
                textTransform: 'uppercase', color,
            }}>
                {label}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: alpha(color, 0.2) }} />
        </Box>
    );
}

// ─── Feature list editor ───────────────────────────────────────────────────────
function FeatureListEditor({ label, color = ACCENT, items, onChange }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [newItem, setNewItem] = useState('');

    const add = () => {
        const trimmed = newItem.trim();
        if (!trimmed) return;
        onChange([...items, trimmed]);
        setNewItem('');
    };

    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

    return (
        <Box sx={{
            borderRadius: '12px',
            border: `1px solid ${alpha(color, 0.2)}`,
            p: 1.5,
            bgcolor: alpha(color, isDark ? 0.04 : 0.03),
        }}>
            <Typography variant="caption" sx={{
                fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.06em',
                textTransform: 'uppercase', color, display: 'block', mb: 1,
            }}>
                {label}
            </Typography>

            {items.length === 0 ? (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                    No features yet
                </Typography>
            ) : (
                <List dense disablePadding sx={{ mb: 1 }}>
                    {items.map((item, idx) => (
                        <ListItem key={idx} disableGutters sx={{
                            py: 0.3, px: 1,
                            borderRadius: '6px',
                            '&:hover': { bgcolor: alpha(color, 0.07) },
                        }}>
                            <Box sx={{
                                width: 5, height: 5, borderRadius: '50%',
                                bgcolor: color, mr: 1, flexShrink: 0,
                            }} />
                            <ListItemText
                                primary={item}
                                primaryTypographyProps={{ variant: 'body2', fontSize: '0.78rem' }}
                            />
                            <ListItemSecondaryAction>
                                <IconButton size="small" onClick={() => remove(idx)}
                                    sx={{ color: RED, opacity: 0.7, '&:hover': { opacity: 1 } }}>
                                    <DeleteIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    size="small" fullWidth
                    placeholder="Add feature…"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && add()}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '0.82rem',
                            '& fieldset': { borderColor: alpha(color, 0.3) },
                            '&:hover fieldset': { borderColor: alpha(color, 0.5) },
                            '&.Mui-focused fieldset': { borderColor: color },
                        },
                    }}
                />
                <Button
                    size="small" variant="contained" onClick={add}
                    disabled={!newItem.trim()}
                    sx={{
                        borderRadius: '8px', minWidth: 48, px: 1.5,
                        bgcolor: color, '&:hover': { bgcolor: alpha(color, 0.85) },
                        boxShadow: 'none',
                    }}
                >
                    <AddIcon sx={{ fontSize: 16 }} />
                </Button>
            </Box>
        </Box>
    );
}

// ─── Edit / Create plan dialog ─────────────────────────────────────────────────
function PlanDialog({ open, planId, initialData, isNew, onClose, onSaved }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [form, setForm] = useState(EMPTY_PLAN);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [newPlanId, setNewPlanId] = useState('');

    useEffect(() => {
        if (open) {
            setForm(initialData ? JSON.parse(JSON.stringify(initialData)) : { ...EMPTY_PLAN });
            setNewPlanId('');
            setError('');
        }
    }, [open, initialData]);

    const set = (path, value) => {
        setForm((prev) => {
            const next = { ...prev };
            const keys = path.split('.');
            let cur = next;
            for (let i = 0; i < keys.length - 1; i++) {
                cur[keys[i]] = { ...cur[keys[i]] };
                cur = cur[keys[i]];
            }
            cur[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const id = isNew ? newPlanId.trim().toLowerCase().replace(/\s+/g, '_') : planId;
            if (!id) { setError('Plan ID is required'); setSaving(false); return; }
            if (!form.name) { setError('Plan name is required'); setSaving(false); return; }
            if (isNew) {
                await adminApi.createPlan(id, form);
            } else {
                await adminApi.updatePlan(id, form);
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            '& fieldset': { borderColor: alpha(theme.palette.divider, 0.4) },
            '&:hover fieldset': { borderColor: alpha(ACCENT, 0.5) },
            '&.Mui-focused fieldset': { borderColor: ACCENT },
        },
        '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    bgcolor: isDark
                        ? alpha('#0f172a', 0.95)
                        : alpha('#ffffff', 0.97),
                    backdropFilter: 'blur(24px)',
                    backgroundImage: 'none',
                    boxShadow: isDark
                        ? `0 25px 60px ${alpha('#000', 0.5)}`
                        : `0 25px 60px ${alpha('#000', 0.12)}`,
                    overflow: 'hidden',
                },
            }}
        >
            {/* Header band */}
            <Box sx={{
                px: 3, pt: 3, pb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: isDark
                    ? `linear-gradient(135deg, ${alpha(ACCENT, 0.12)} 0%, ${alpha('#0f172a', 0)} 60%)`
                    : `linear-gradient(135deg, ${alpha(ACCENT, 0.06)} 0%, transparent 60%)`,
            }}>
                <Box>
                    <Typography variant="overline" sx={{ color: ACCENT, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.12em' }}>
                        {isNew ? 'New Plan' : 'Edit Plan'}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.25 }}>
                        {isNew ? 'Create New Plan' : `Edit — ${planId}`}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 3, pt: 2.5 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* ── IDENTITY ── */}
                <SectionLabel icon={<LabelIcon />} label="Identity" color={ACCENT} />
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {isNew && (
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Plan ID"
                                size="small" fullWidth
                                value={newPlanId}
                                onChange={(e) => setNewPlanId(e.target.value)}
                                helperText="e.g. pro, enterprise — cannot change later"
                                placeholder="pro"
                                sx={fieldSx}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={isNew ? 4 : 6}>
                        <TextField
                            label="Display Name"
                            size="small" fullWidth
                            value={form.name}
                            onChange={(e) => set('name', e.target.value)}
                            placeholder="Pro"
                            sx={fieldSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={isNew ? 4 : 6}>
                        <TextField
                            label="Tagline"
                            size="small" fullWidth
                            value={form.target || ''}
                            onChange={(e) => set('target', e.target.value)}
                            placeholder="For growing creators"
                            sx={fieldSx}
                        />
                    </Grid>
                </Grid>

                {/* ── PRICING ── */}
                <SectionLabel icon={<PriceIcon />} label="Pricing & Stripe" color={GREEN} />
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            label="USD Price" size="small" fullWidth
                            value={form.price?.usd || ''}
                            onChange={(e) => set('price.usd', e.target.value)}
                            placeholder="$9"
                            InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ color: GREEN, fontWeight: 700 }}>$</Typography></InputAdornment> }}
                            sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], '&.Mui-focused fieldset': { borderColor: GREEN } }, '& .MuiInputLabel-root.Mui-focused': { color: GREEN } }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            label="INR Price" size="small" fullWidth
                            value={form.price?.inr || ''}
                            onChange={(e) => set('price.inr', e.target.value)}
                            placeholder="₹199"
                            InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ color: GREEN, fontWeight: 700 }}>₹</Typography></InputAdornment> }}
                            sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], '&.Mui-focused fieldset': { borderColor: GREEN } }, '& .MuiInputLabel-root.Mui-focused': { color: GREEN } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Stripe Price ID (USD)" size="small" fullWidth
                            value={form.priceIds?.usd || ''}
                            onChange={(e) => set('priceIds.usd', e.target.value)}
                            placeholder="price_xxx"
                            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                            sx={fieldSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Stripe Price ID (INR)" size="small" fullWidth
                            value={form.priceIds?.inr || ''}
                            onChange={(e) => set('priceIds.inr', e.target.value)}
                            placeholder="price_yyy"
                            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                            sx={fieldSx}
                        />
                    </Grid>
                </Grid>

                {/* ── RAZORPAY ── */}
                <SectionLabel icon={<PriceIcon />} label="Razorpay (INR)" color="#3395ff" />
                <Box sx={{ borderRadius: '12px', border: '1px solid rgba(51,149,255,0.2)', bgcolor: 'rgba(51,149,255,0.03)', p: 2, mb: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Razorpay Plan ID" size="small" fullWidth
                                value={form.razorpayPlanId || ''}
                                onChange={(e) => set('razorpayPlanId', e.target.value)}
                                placeholder="plan_xxxxxxxxxxxx"
                                inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                                helperText="Create in Razorpay Dashboard → Plans, then paste the plan_xxx ID here."
                                sx={fieldSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Amount (paise)" size="small" fullWidth
                                type="number"
                                value={form.razorpayAmountPaise || ''}
                                onChange={(e) => set('razorpayAmountPaise', e.target.value)}
                                placeholder="49900"
                                inputProps={{ min: 0, style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                                helperText="INR price × 100. E.g. ₹499 → 49900. Used to verify payment amount server-side."
                                sx={fieldSx}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* ── QUOTA ── */}
                <SectionLabel icon={<QuotaIcon />} label="Monthly Quota" color={AMBER} />
                <Box sx={{
                    borderRadius: '12px',
                    border: `1px solid ${alpha(AMBER, 0.25)}`,
                    bgcolor: alpha(AMBER, isDark ? 0.05 : 0.04),
                    p: 2, mb: 3,
                }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="USD Users Quota" size="small" fullWidth
                                type="number" inputProps={{ min: 0 }}
                                value={form.quota?.usd ?? ''}
                                onChange={(e) => set('quota.usd', parseInt(e.target.value, 10) || 0)}
                                helperText="AI generations / month"
                                sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], '&.Mui-focused fieldset': { borderColor: AMBER } }, '& .MuiInputLabel-root.Mui-focused': { color: AMBER } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="INR Users Quota" size="small" fullWidth
                                type="number" inputProps={{ min: 0 }}
                                value={form.quota?.inr ?? ''}
                                onChange={(e) => set('quota.inr', parseInt(e.target.value, 10) || 0)}
                                helperText="AI generations / month"
                                sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], '&.Mui-focused fieldset': { borderColor: AMBER } }, '& .MuiInputLabel-root.Mui-focused': { color: AMBER } }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* ── DAILY ENGAGEMENT LIMIT ── */}
                <SectionLabel icon={<QuotaIcon />} label="Daily Engagement Limit" color={GREEN} />
                <Box sx={{
                    borderRadius: '12px',
                    border: `1px solid ${alpha(GREEN, 0.25)}`,
                    bgcolor: alpha(GREEN, isDark ? 0.05 : 0.04),
                    p: 2, mb: 3,
                }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="USD Users Daily Limit" size="small" fullWidth
                                type="number" inputProps={{ min: 0 }}
                                value={form.engagement_daily?.usd ?? ''}
                                onChange={(e) => set('engagement_daily.usd', parseInt(e.target.value, 10) || 0)}
                                helperText="Engagement replies / day"
                                sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], '&.Mui-focused fieldset': { borderColor: GREEN } }, '& .MuiInputLabel-root.Mui-focused': { color: GREEN } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="INR Users Daily Limit" size="small" fullWidth
                                type="number" inputProps={{ min: 0 }}
                                value={form.engagement_daily?.inr ?? ''}
                                onChange={(e) => set('engagement_daily.inr', parseInt(e.target.value, 10) || 0)}
                                helperText="Engagement replies / day"
                                sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], '&.Mui-focused fieldset': { borderColor: GREEN } }, '& .MuiInputLabel-root.Mui-focused': { color: GREEN } }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* ── APPEARANCE ── */}
                <SectionLabel icon={<PaletteIcon />} label="Appearance & Visibility" color='#a855f7' />
                <Box sx={{
                    borderRadius: '12px',
                    border: `1px solid ${alpha('#a855f7', 0.2)}`,
                    bgcolor: alpha('#a855f7', isDark ? 0.04 : 0.03),
                    p: 2, mb: 3,
                }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            {/* Accent color — real color picker */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                    <Box
                                        component="input"
                                        type="color"
                                        value={form.accent || '#6366f1'}
                                        onChange={(e) => set('accent', e.target.value)}
                                        sx={{
                                            width: 44, height: 44,
                                            borderRadius: '10px',
                                            border: `2px solid ${alpha('#a855f7', 0.3)}`,
                                            cursor: 'pointer',
                                            p: 0.25,
                                            bgcolor: 'transparent',
                                            '&::-webkit-color-swatch-wrapper': { p: 0 },
                                            '&::-webkit-color-swatch': { borderRadius: '7px', border: 'none' },
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
                                        Accent Color
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: form.accent }}>
                                        {form.accent || '#6366f1'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Badge (optional)" size="small" fullWidth
                                value={form.badge || ''}
                                onChange={(e) => set('badge', e.target.value)}
                                placeholder="MOST POPULAR"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><LabelIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>,
                                }}
                                sx={fieldSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.active !== false}
                                            onChange={(e) => set('active', e.target.checked)}
                                            size="small"
                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: GREEN }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GREEN } }}
                                        />
                                    }
                                    label={<Typography variant="body2" sx={{ fontSize: '0.82rem' }}>Active (visible to users)</Typography>}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.outlined === true}
                                            onChange={(e) => set('outlined', e.target.checked)}
                                            size="small"
                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                                        />
                                    }
                                    label={<Typography variant="body2" sx={{ fontSize: '0.82rem' }}>Outlined card style</Typography>}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* ── FEATURES ── */}
                <SectionLabel icon={<FeaturesIcon />} label="Features" color='#06b6d4' />
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <FeatureListEditor
                            label="Global / USD Features"
                            color='#06b6d4'
                            items={form.features_global || []}
                            onChange={(v) => set('features_global', v)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FeatureListEditor
                            label="India / INR Features"
                            color='#f97316'
                            items={form.features_ind || []}
                            onChange={(v) => set('features_ind', v)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            {/* Footer */}
            <Box sx={{
                px: 3, py: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5,
                bgcolor: isDark ? alpha('#000', 0.2) : alpha('#f8fafc', 0.8),
            }}>
                <Button
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                        borderRadius: '10px', px: 2.5,
                        color: 'text.secondary',
                        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                        '&:hover': { bgcolor: alpha(RED, 0.06), borderColor: alpha(RED, 0.3), color: RED },
                    }}
                    startIcon={<CancelIcon />}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
                    sx={{
                        borderRadius: '10px', px: 3,
                        bgcolor: GREEN, '&:hover': { bgcolor: alpha(GREEN, 0.85) },
                        boxShadow: `0 4px 14px ${alpha(GREEN, 0.35)}`,
                        fontWeight: 700,
                    }}
                >
                    {isNew ? 'Create Plan' : 'Save Changes'}
                </Button>
            </Box>
        </Dialog>
    );
}

// ─── Engagement top-up grant dialog ───────────────────────────────────────────
function EngagementTopupDialog({ open, onClose }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [query, setQuery]       = useState('');
    const [searching, setSearching] = useState(false);
    const [results, setResults]   = useState([]);
    const [selected, setSelected] = useState(null);
    const [credits, setCredits]   = useState('');
    const [saving, setSaving]     = useState(false);
    const [result, setResult]     = useState(null);

    useEffect(() => {
        if (!open) return;
        if (query.trim().length < 2) { setResults([]); return; }
        const t = setTimeout(async () => {
            setSearching(true);
            try { setResults(await adminApi.searchUsers(query.trim())); }
            catch { setResults([]); }
            finally { setSearching(false); }
        }, 350);
        return () => clearTimeout(t);
    }, [query, open]);

    useEffect(() => {
        if (open) { setQuery(''); setResults([]); setSelected(null); setCredits(''); setResult(null); }
    }, [open]);

    const handleSelect = (user) => {
        setSelected(user);
        setQuery('');
        setResults([]);
    };

    const handleGrant = async () => {
        if (!selected) return;
        const n = parseInt(credits, 10);
        if (!n || n < 1) { setResult({ type: 'error', message: 'Enter a positive number of credits' }); return; }
        setSaving(true);
        setResult(null);
        try {
            const data = await adminApi.grantEngagementTopup(selected.uid, n);
            setResult({ type: 'success', message: `${data.credits_added} top-up credit(s) granted successfully.` });
            setSelected(s => ({ ...s, engagement_topup_remaining: (s.engagement_topup_remaining ?? 0) + data.credits_added }));
            setCredits('');
        } catch (err) {
            setResult({ type: 'error', message: err.message || 'Failed' });
        } finally {
            setSaving(false);
        }
    };

    const PLAN_COLOR = { free: '#94a3b8', starter: GREEN, pro: ACCENT, agency: '#a855f7', enterprise: AMBER };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    bgcolor: isDark ? alpha('#0f172a', 0.97) : alpha('#ffffff', 0.98),
                    backdropFilter: 'blur(24px)',
                    backgroundImage: 'none',
                    boxShadow: isDark ? `0 25px 60px ${alpha('#000', 0.5)}` : `0 25px 60px ${alpha('#000', 0.12)}`,
                    overflow: 'visible',
                },
            }}>

            {/* Header */}
            <Box sx={{
                px: 3, pt: 3, pb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: isDark
                    ? `linear-gradient(135deg, ${alpha(GREEN, 0.1)} 0%, transparent 60%)`
                    : `linear-gradient(135deg, ${alpha(GREEN, 0.05)} 0%, transparent 60%)`,
                borderRadius: '20px 20px 0 0',
            }}>
                <Box>
                    <Typography variant="overline" sx={{ color: GREEN, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.12em' }}>
                        Admin Tool
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.25 }}>
                        Grant Engagement Top-Up
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 3, overflow: 'visible' }}>
                {result && (
                    <Alert severity={result.type} sx={{ mb: 2.5, borderRadius: '10px' }} onClose={() => setResult(null)}>
                        {result.message}
                    </Alert>
                )}

                {/* Step 1: Search */}
                <Box sx={{ mb: selected ? 2.5 : 0 }}>
                    <Typography variant="caption" sx={{
                        fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
                        textTransform: 'uppercase', color: GREEN, display: 'block', mb: 1,
                    }}>
                        {selected ? '① User Selected' : '① Search User'}
                    </Typography>

                    {selected ? (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            p: 1.5, borderRadius: '12px',
                            border: `1px solid ${alpha(GREEN, 0.3)}`,
                            bgcolor: alpha(GREEN, isDark ? 0.06 : 0.04),
                        }}>
                            <Box sx={{
                                width: 40, height: 40, borderRadius: '50%',
                                bgcolor: alpha(GREEN, 0.15),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                overflow: 'hidden', flexShrink: 0,
                            }}>
                                {selected.photoURL
                                    ? <Box component="img" src={selected.photoURL} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    : <PersonIcon sx={{ color: GREEN, fontSize: 22 }} />
                                }
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                        {selected.displayName || 'No name'}
                                    </Typography>
                                    <Chip label={selected.plan || 'free'} size="small"
                                        sx={{ height: 18, borderRadius: '5px', fontSize: '0.62rem', fontWeight: 700,
                                            bgcolor: alpha(PLAN_COLOR[selected.plan] || ACCENT, 0.15),
                                            color: PLAN_COLOR[selected.plan] || ACCENT }}
                                    />
                                    {selected.engagement_topup_remaining > 0 && (
                                        <Chip label={`topup: ${selected.engagement_topup_remaining}`} size="small"
                                            sx={{ height: 18, borderRadius: '5px', fontSize: '0.62rem', fontWeight: 700,
                                                bgcolor: alpha(GREEN, 0.15), color: GREEN }}
                                        />
                                    )}
                                </Box>
                                <Typography variant="caption" color="text.secondary" noWrap>{selected.email}</Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled', display: 'block', fontSize: '0.68rem' }} noWrap>
                                    {selected.uid}
                                </Typography>
                            </Box>
                            <Tooltip title="Change user">
                                <IconButton size="small" onClick={() => { setSelected(null); setCredits(''); setResult(null); }}
                                    sx={{ color: 'text.secondary', '&:hover': { color: RED } }}>
                                    <ClearIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 1.5, py: 1,
                                borderRadius: '12px',
                                border: `1px solid ${query.length >= 2 ? alpha(GREEN, 0.5) : alpha(theme.palette.divider, 0.35)}`,
                                bgcolor: query.length >= 2 ? alpha(GREEN, isDark ? 0.05 : 0.03) : 'transparent',
                                transition: 'all 0.2s',
                            }}>
                                {searching
                                    ? <CircularProgress size={15} sx={{ color: GREEN, flexShrink: 0 }} />
                                    : <SearchIcon sx={{ fontSize: 18, color: query.length >= 2 ? GREEN : 'text.disabled', flexShrink: 0 }} />
                                }
                                <Box component="input"
                                    placeholder="Search by name or email…"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    sx={{
                                        border: 'none', outline: 'none',
                                        bgcolor: 'transparent', color: 'inherit',
                                        fontSize: '0.9rem', fontFamily: 'inherit',
                                        flex: 1, minWidth: 0,
                                        '&::placeholder': { color: 'text.disabled', opacity: 0.6 },
                                    }}
                                />
                                {query && (
                                    <IconButton size="small" onClick={() => { setQuery(''); setResults([]); }}
                                        sx={{ p: 0.25, color: 'text.disabled' }}>
                                        <ClearIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                )}
                            </Box>
                            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                Type at least 2 characters to search
                            </Typography>

                            {results.length > 0 && (
                                <Box sx={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                                    mt: 0.5, borderRadius: '14px',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                    bgcolor: isDark ? alpha('#1e293b', 0.98) : alpha('#ffffff', 0.99),
                                    backdropFilter: 'blur(20px)',
                                    boxShadow: isDark ? `0 12px 40px ${alpha('#000', 0.5)}` : `0 12px 40px ${alpha('#000', 0.12)}`,
                                    overflow: 'hidden', maxHeight: 300, overflowY: 'auto',
                                }}>
                                    {results.map((user, idx) => (
                                        <Box key={user.uid} onClick={() => handleSelect(user)} sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            px: 2, py: 1.25, cursor: 'pointer',
                                            borderBottom: idx < results.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.07)}` : 'none',
                                            '&:hover': { bgcolor: alpha(GREEN, isDark ? 0.08 : 0.05) },
                                            transition: 'background 0.15s',
                                        }}>
                                            <Box sx={{
                                                width: 36, height: 36, borderRadius: '50%',
                                                bgcolor: alpha(GREEN, 0.12),
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                overflow: 'hidden', flexShrink: 0,
                                            }}>
                                                {user.photoURL
                                                    ? <Box component="img" src={user.photoURL} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    : <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: GREEN }}>
                                                        {(user.displayName || user.email || '?')[0].toUpperCase()}
                                                    </Typography>
                                                }
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                                        {user.displayName || <Typography component="span" variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>No name</Typography>}
                                                    </Typography>
                                                    <Chip label={user.plan || 'free'} size="small"
                                                        sx={{ height: 16, borderRadius: '4px', fontSize: '0.6rem', fontWeight: 700,
                                                            bgcolor: alpha(PLAN_COLOR[user.plan] || ACCENT, 0.15),
                                                            color: PLAN_COLOR[user.plan] || ACCENT }}
                                                    />
                                                    {(user.engagement_topup_remaining ?? 0) > 0 && (
                                                        <Chip label={`topup: ${user.engagement_topup_remaining}`} size="small"
                                                            sx={{ height: 16, borderRadius: '4px', fontSize: '0.6rem', fontWeight: 700,
                                                                bgcolor: alpha(GREEN, 0.12), color: GREEN }}
                                                        />
                                                    )}
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" noWrap>{user.email}</Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {query.length >= 2 && !searching && results.length === 0 && (
                                <Box sx={{ mt: 0.5, p: 2.5, borderRadius: '12px', textAlign: 'center', border: `1px dashed ${alpha(theme.palette.divider, 0.25)}` }}>
                                    <Typography variant="body2" color="text.secondary">No users found for "{query}"</Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Step 2: Grant credits */}
                {selected && (
                    <Box>
                        <Typography variant="caption" sx={{
                            fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: GREEN, display: 'block', mb: 1.5,
                        }}>
                            ② Credits to Grant
                        </Typography>
                        <TextField
                            size="small" fullWidth
                            label="Top-Up Credits"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={credits}
                            onChange={e => setCredits(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleGrant()}
                            placeholder="e.g. 200"
                            helperText={`Current top-up balance: ${selected.engagement_topup_remaining ?? 0} — credits roll over until exhausted`}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <QuotaIcon sx={{ fontSize: 16, color: GREEN }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    '& fieldset': { borderColor: alpha(GREEN, 0.3) },
                                    '&:hover fieldset': { borderColor: alpha(GREEN, 0.5) },
                                    '&.Mui-focused fieldset': { borderColor: GREEN },
                                },
                                '& .MuiInputLabel-root.Mui-focused': { color: GREEN },
                                '& .MuiFormHelperText-root': { fontSize: '0.7rem' },
                            }}
                        />
                    </Box>
                )}
            </DialogContent>

            {/* Footer */}
            <Box sx={{
                px: 3, py: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex', gap: 1.5, justifyContent: 'flex-end',
                bgcolor: isDark ? alpha('#000', 0.2) : alpha('#f8fafc', 0.8),
                borderRadius: '0 0 20px 20px',
            }}>
                <Button onClick={onClose} disabled={saving}
                    sx={{ borderRadius: '10px', color: 'text.secondary', border: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
                    Close
                </Button>
                <Button
                    onClick={handleGrant}
                    variant="contained"
                    disabled={saving || !selected || !credits}
                    startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <QuotaIcon />}
                    sx={{
                        borderRadius: '10px', fontWeight: 700,
                        bgcolor: GREEN, '&:hover': { bgcolor: alpha(GREEN, 0.85) },
                        boxShadow: `0 4px 14px ${alpha(GREEN, 0.3)}`,
                    }}
                >
                    Grant Credits
                </Button>
            </Box>
        </Dialog>
    );
}

// ─── Per-user quota override dialog ───────────────────────────────────────────
function UserQuotaDialog({ open, onClose }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // search state
    const [query, setQuery]         = useState('');
    const [searching, setSearching] = useState(false);
    const [results, setResults]     = useState([]);
    const [selected, setSelected]   = useState(null); // { uid, email, displayName, plan, custom_quota_limit }

    // quota state
    const [quota, setQuota]   = useState('');
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);

    // debounce search
    useEffect(() => {
        if (!open) return;
        if (query.trim().length < 2) { setResults([]); return; }
        const t = setTimeout(async () => {
            setSearching(true);
            try { setResults(await adminApi.searchUsers(query.trim())); }
            catch { setResults([]); }
            finally { setSearching(false); }
        }, 350);
        return () => clearTimeout(t);
    }, [query, open]);

    useEffect(() => {
        if (open) { setQuery(''); setResults([]); setSelected(null); setQuota(''); setResult(null); }
    }, [open]);

    const handleSelect = (user) => {
        setSelected(user);
        setQuery('');
        setResults([]);
        setQuota(user.custom_quota_limit != null ? String(user.custom_quota_limit) : '');
    };

    const handleApply = async () => {
        if (!selected) return;
        setSaving(true);
        setResult(null);
        try {
            const limit = quota === '' ? null : parseInt(quota, 10);
            const data = await adminApi.overrideUserQuota(selected.uid, limit);
            setResult({ type: 'success', message: data.message });
            setSelected(s => ({ ...s, custom_quota_limit: limit }));
        } catch (err) {
            setResult({ type: 'error', message: err.message || 'Failed' });
        } finally {
            setSaving(false);
        }
    };

    const PLAN_COLOR = { free: '#94a3b8', starter: GREEN, pro: ACCENT, agency: '#a855f7', enterprise: AMBER };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    bgcolor: isDark ? alpha('#0f172a', 0.97) : alpha('#ffffff', 0.98),
                    backdropFilter: 'blur(24px)',
                    backgroundImage: 'none',
                    boxShadow: isDark ? `0 25px 60px ${alpha('#000', 0.5)}` : `0 25px 60px ${alpha('#000', 0.12)}`,
                    overflow: 'visible',
                },
            }}>

            {/* Header */}
            <Box sx={{
                px: 3, pt: 3, pb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: isDark
                    ? `linear-gradient(135deg, ${alpha(AMBER, 0.1)} 0%, transparent 60%)`
                    : `linear-gradient(135deg, ${alpha(AMBER, 0.05)} 0%, transparent 60%)`,
                borderRadius: '20px 20px 0 0',
            }}>
                <Box>
                    <Typography variant="overline" sx={{ color: AMBER, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.12em' }}>
                        Admin Tool
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.25 }}>
                        Override User Quota
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 3, overflow: 'visible' }}>
                {result && (
                    <Alert severity={result.type} sx={{ mb: 2.5, borderRadius: '10px' }} onClose={() => setResult(null)}>
                        {result.message}
                    </Alert>
                )}

                {/* ── Step 1: Search ── */}
                <Box sx={{ mb: selected ? 2.5 : 0 }}>
                    <Typography variant="caption" sx={{
                        fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
                        textTransform: 'uppercase', color: AMBER, display: 'block', mb: 1,
                    }}>
                        {selected ? '① User Selected' : '① Search User'}
                    </Typography>

                    {/* Selected user card */}
                    {selected ? (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            p: 1.5, borderRadius: '12px',
                            border: `1px solid ${alpha(GREEN, 0.3)}`,
                            bgcolor: alpha(GREEN, isDark ? 0.06 : 0.04),
                        }}>
                            {/* Avatar */}
                            <Box sx={{
                                width: 40, height: 40, borderRadius: '50%',
                                bgcolor: alpha(ACCENT, 0.15),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                overflow: 'hidden', flexShrink: 0,
                            }}>
                                {selected.photoURL
                                    ? <Box component="img" src={selected.photoURL} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    : <PersonIcon sx={{ color: ACCENT, fontSize: 22 }} />
                                }
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                        {selected.displayName || 'No name'}
                                    </Typography>
                                    <Chip
                                        label={selected.plan || 'free'}
                                        size="small"
                                        sx={{
                                            height: 18, borderRadius: '5px', fontSize: '0.62rem', fontWeight: 700,
                                            bgcolor: alpha(PLAN_COLOR[selected.plan] || ACCENT, 0.15),
                                            color: PLAN_COLOR[selected.plan] || ACCENT,
                                        }}
                                    />
                                </Box>
                                <Typography variant="caption" color="text.secondary" noWrap>{selected.email}</Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled', display: 'block', fontSize: '0.68rem' }} noWrap>
                                    {selected.uid}
                                </Typography>
                            </Box>
                            <Tooltip title="Change user">
                                <IconButton size="small" onClick={() => { setSelected(null); setQuota(''); setResult(null); }}
                                    sx={{ color: 'text.secondary', '&:hover': { color: RED } }}>
                                    <ClearIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        /* Search input + dropdown */
                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 1.5, py: 1,
                                borderRadius: '12px',
                                border: `1px solid ${query.length >= 2 ? alpha(AMBER, 0.5) : alpha(theme.palette.divider, 0.35)}`,
                                bgcolor: query.length >= 2 ? alpha(AMBER, isDark ? 0.05 : 0.03) : 'transparent',
                                transition: 'all 0.2s',
                            }}>
                                {searching
                                    ? <CircularProgress size={15} sx={{ color: AMBER, flexShrink: 0 }} />
                                    : <SearchIcon sx={{ fontSize: 18, color: query.length >= 2 ? AMBER : 'text.disabled', flexShrink: 0 }} />
                                }
                                <Box
                                    component="input"
                                    placeholder="Search by name or email…"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    sx={{
                                        border: 'none', outline: 'none',
                                        bgcolor: 'transparent', color: 'inherit',
                                        fontSize: '0.9rem', fontFamily: 'inherit',
                                        flex: 1, minWidth: 0,
                                        '&::placeholder': { color: 'text.disabled', opacity: 0.6 },
                                    }}
                                />
                                {query && (
                                    <IconButton size="small" onClick={() => { setQuery(''); setResults([]); }}
                                        sx={{ p: 0.25, color: 'text.disabled' }}>
                                        <ClearIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                )}
                            </Box>
                            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                Type at least 2 characters to search across all users
                            </Typography>

                            {/* Dropdown results */}
                            {results.length > 0 && (
                                <Box sx={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                                    mt: 0.5, borderRadius: '14px',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                    bgcolor: isDark ? alpha('#1e293b', 0.98) : alpha('#ffffff', 0.99),
                                    backdropFilter: 'blur(20px)',
                                    boxShadow: isDark ? `0 12px 40px ${alpha('#000', 0.5)}` : `0 12px 40px ${alpha('#000', 0.12)}`,
                                    overflow: 'hidden',
                                    maxHeight: 300, overflowY: 'auto',
                                }}>
                                    {results.map((user, idx) => (
                                        <Box
                                            key={user.uid}
                                            onClick={() => handleSelect(user)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                px: 2, py: 1.25,
                                                cursor: 'pointer',
                                                borderBottom: idx < results.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.07)}` : 'none',
                                                '&:hover': { bgcolor: alpha(AMBER, isDark ? 0.08 : 0.05) },
                                                transition: 'background 0.15s',
                                            }}
                                        >
                                            {/* Avatar */}
                                            <Box sx={{
                                                width: 36, height: 36, borderRadius: '50%',
                                                bgcolor: alpha(ACCENT, 0.12),
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                overflow: 'hidden', flexShrink: 0,
                                            }}>
                                                {user.photoURL
                                                    ? <Box component="img" src={user.photoURL} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    : <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: ACCENT }}>
                                                        {(user.displayName || user.email || '?')[0].toUpperCase()}
                                                    </Typography>
                                                }
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                                        {user.displayName || <Typography component="span" variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>No name</Typography>}
                                                    </Typography>
                                                    <Chip
                                                        label={user.plan || 'free'}
                                                        size="small"
                                                        sx={{
                                                            height: 16, borderRadius: '4px', fontSize: '0.6rem', fontWeight: 700,
                                                            bgcolor: alpha(PLAN_COLOR[user.plan] || ACCENT, 0.15),
                                                            color: PLAN_COLOR[user.plan] || ACCENT,
                                                        }}
                                                    />
                                                    {user.custom_quota_limit != null && (
                                                        <Chip
                                                            label={`quota: ${user.custom_quota_limit}`}
                                                            size="small"
                                                            sx={{ height: 16, borderRadius: '4px', fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha(AMBER, 0.12), color: AMBER }}
                                                        />
                                                    )}
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" noWrap>{user.email}</Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {query.length >= 2 && !searching && results.length === 0 && (
                                <Box sx={{
                                    mt: 0.5, p: 2.5, borderRadius: '12px', textAlign: 'center',
                                    border: `1px dashed ${alpha(theme.palette.divider, 0.25)}`,
                                }}>
                                    <Typography variant="body2" color="text.secondary">No users found for "{query}"</Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                {/* ── Step 2: Set quota (only when user is selected) ── */}
                {selected && (
                    <Box>
                        <Typography variant="caption" sx={{
                            fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: ACCENT, display: 'block', mb: 1.5,
                        }}>
                            ② Set Custom Quota
                        </Typography>
                        <TextField
                            size="small" fullWidth
                            label="Custom Monthly AI Quota"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={quota}
                            onChange={e => setQuota(e.target.value)}
                            placeholder="e.g. 500"
                            helperText={
                                selected.custom_quota_limit != null
                                    ? `Current override: ${selected.custom_quota_limit} — leave blank to clear and revert to plan default`
                                    : 'No override active — leave blank to keep plan default'
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <QuotaIcon sx={{ fontSize: 16, color: ACCENT }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    '& fieldset': { borderColor: alpha(ACCENT, 0.3) },
                                    '&:hover fieldset': { borderColor: alpha(ACCENT, 0.5) },
                                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                                },
                                '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
                                '& .MuiFormHelperText-root': { fontSize: '0.7rem' },
                            }}
                        />
                    </Box>
                )}
            </DialogContent>

            {/* Footer */}
            <Box sx={{
                px: 3, py: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex', gap: 1.5, justifyContent: 'flex-end',
                bgcolor: isDark ? alpha('#000', 0.2) : alpha('#f8fafc', 0.8),
                borderRadius: '0 0 20px 20px',
            }}>
                <Button onClick={onClose} disabled={saving}
                    sx={{ borderRadius: '10px', color: 'text.secondary', border: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
                    Close
                </Button>
                <Button
                    onClick={handleApply}
                    variant="contained"
                    disabled={saving || !selected}
                    startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <PersonIcon />}
                    sx={{
                        borderRadius: '10px', fontWeight: 700,
                        bgcolor: AMBER, '&:hover': { bgcolor: alpha(AMBER, 0.85) },
                        boxShadow: `0 4px 14px ${alpha(AMBER, 0.3)}`,
                    }}
                >
                    Apply Override
                </Button>
            </Box>
        </Dialog>
    );
}

// ─── Manage User (role + plan) dialog ─────────────────────────────────────────
const ROLE_OPTIONS = [
    {
        value: 'super_admin',
        label: 'Super Admin',
        description: 'Full access — can manage plans, users, billing, toggles',
        color: RED,
        icon: <ShieldIcon />,
    },
    {
        value: 'admin',
        label: 'Admin',
        description: 'Read-only admin access — can view dashboards and reports',
        color: AMBER,
        icon: <AdminIcon />,
    },
    {
        value: 'revoke',
        label: 'Regular User',
        description: 'No admin privileges — standard plan-based access',
        color: '#94a3b8',
        icon: <PersonIcon />,
    },
];

function ManageUserDialog({ open, onClose, plans, onNotify }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // search state
    const [query, setQuery]         = useState('');
    const [searching, setSearching] = useState(false);
    const [results, setResults]     = useState([]);
    const [selected, setSelected]   = useState(null);

    // form state
    const [role, setRole]   = useState('');
    const [plan, setPlan]   = useState('');
    const [saving, setSaving] = useState(false);

    // derive current role from selected user's claims (returned by search_users)
    const currentRole = selected?.claims?.super_admin
        ? 'super_admin'
        : selected?.claims?.admin
        ? 'admin'
        : 'revoke';

    useEffect(() => {
        if (!open) return;
        if (query.trim().length < 2) { setResults([]); return; }
        const t = setTimeout(async () => {
            setSearching(true);
            try { setResults(await adminApi.searchUsers(query.trim())); }
            catch { setResults([]); }
            finally { setSearching(false); }
        }, 350);
        return () => clearTimeout(t);
    }, [query, open]);

    useEffect(() => {
        if (open) { setQuery(''); setResults([]); setSelected(null); setRole(''); setPlan(''); }
    }, [open]);

    const handleSelect = (user) => {
        setSelected(user);
        setQuery('');
        setResults([]);
        const r = user.claims?.super_admin ? 'super_admin' : user.claims?.admin ? 'admin' : 'revoke';
        setRole(r);
        setPlan(user.plan || 'free');
    };

    const handleSave = async () => {
        if (!selected || !role) return;
        setSaving(true);
        try {
            const payload = { uid: selected.uid, role };
            if (plan && plan !== selected.plan) payload.plan = plan;
            const res = await adminApi.updateUserRole(payload);
            onNotify('success', res.message || 'User updated successfully.');
            // Update local state to reflect new role
            setSelected(s => ({
                ...s,
                plan: plan || s.plan,
                claims: res.claims || s.claims,
            }));
        } catch (err) {
            onNotify('error', err.message || 'Failed to update user.');
        } finally {
            setSaving(false);
        }
    };

    const PLAN_COLOR = { free: '#94a3b8', starter: GREEN, pro: ACCENT, agency: '#a855f7', enterprise: AMBER };

    const fieldSx = (focusColor = ACCENT) => ({
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            '& fieldset': { borderColor: alpha(theme.palette.divider, 0.3) },
            '&:hover fieldset': { borderColor: alpha(focusColor, 0.5) },
            '&.Mui-focused fieldset': { borderColor: focusColor },
        },
        '& .MuiInputLabel-root.Mui-focused': { color: focusColor },
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    bgcolor: isDark ? alpha('#0f172a', 0.97) : alpha('#ffffff', 0.98),
                    backdropFilter: 'blur(24px)',
                    backgroundImage: 'none',
                    boxShadow: isDark ? `0 25px 60px ${alpha('#000', 0.5)}` : `0 25px 60px ${alpha('#000', 0.12)}`,
                    overflow: 'visible',
                },
            }}>

            {/* Header */}
            <Box sx={{
                px: 3, pt: 3, pb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: isDark
                    ? `linear-gradient(135deg, ${alpha(RED, 0.08)} 0%, transparent 60%)`
                    : `linear-gradient(135deg, ${alpha(RED, 0.04)} 0%, transparent 60%)`,
                borderRadius: '20px 20px 0 0',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: alpha(RED, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AdminIcon sx={{ color: RED, fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography variant="overline" sx={{ color: RED, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.12em' }}>
                            Admin Tool
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            Manage User Access
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 3, overflow: 'visible' }}>

                {/* ── Step 1: Search ── */}
                <Box sx={{ mb: selected ? 3 : 0 }}>
                    <Typography variant="caption" sx={{
                        fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
                        textTransform: 'uppercase', color: RED, display: 'block', mb: 1,
                    }}>
                        {selected ? '① User Selected' : '① Search User'}
                    </Typography>

                    {selected ? (
                        /* Selected user card */
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            p: 1.5, borderRadius: '12px',
                            border: `1px solid ${alpha(GREEN, 0.3)}`,
                            bgcolor: alpha(GREEN, isDark ? 0.06 : 0.04),
                        }}>
                            <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: alpha(ACCENT, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                {selected.photoURL
                                    ? <Box component="img" src={selected.photoURL} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    : <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: ACCENT }}>{(selected.displayName || selected.email || '?')[0].toUpperCase()}</Typography>
                                }
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                        {selected.displayName || 'No name'}
                                    </Typography>
                                    {currentRole !== 'revoke' && (
                                        <Chip
                                            label={currentRole === 'super_admin' ? 'Super Admin' : 'Admin'}
                                            size="small"
                                            sx={{ height: 18, borderRadius: '5px', fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha(currentRole === 'super_admin' ? RED : AMBER, 0.15), color: currentRole === 'super_admin' ? RED : AMBER }}
                                        />
                                    )}
                                    <Chip label={selected.plan || 'free'} size="small"
                                        sx={{ height: 18, borderRadius: '5px', fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha(PLAN_COLOR[selected.plan] || ACCENT, 0.15), color: PLAN_COLOR[selected.plan] || ACCENT }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary" noWrap>{selected.email}</Typography>
                            </Box>
                            <Tooltip title="Change user">
                                <IconButton size="small" onClick={() => { setSelected(null); setRole(''); setPlan(''); }}
                                    sx={{ color: 'text.disabled', '&:hover': { color: RED } }}>
                                    <ClearIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        /* Search input + dropdown */
                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 1.5, py: 1,
                                borderRadius: '12px',
                                border: `1px solid ${query.length >= 2 ? alpha(RED, 0.5) : alpha(theme.palette.divider, 0.35)}`,
                                bgcolor: query.length >= 2 ? alpha(RED, isDark ? 0.05 : 0.03) : 'transparent',
                                transition: 'all 0.2s',
                            }}>
                                {searching
                                    ? <CircularProgress size={15} sx={{ color: RED, flexShrink: 0 }} />
                                    : <SearchIcon sx={{ fontSize: 18, color: query.length >= 2 ? RED : 'text.disabled', flexShrink: 0 }} />
                                }
                                <Box component="input" placeholder="Search by name or email…" value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    sx={{ border: 'none', outline: 'none', bgcolor: 'transparent', color: 'inherit', fontSize: '0.9rem', fontFamily: 'inherit', flex: 1, minWidth: 0, '&::placeholder': { color: 'text.disabled', opacity: 0.6 } }}
                                />
                                {query && (
                                    <IconButton size="small" onClick={() => { setQuery(''); setResults([]); }} sx={{ p: 0.25, color: 'text.disabled' }}>
                                        <ClearIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                )}
                            </Box>
                            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                Type at least 2 characters to search
                            </Typography>

                            {results.length > 0 && (
                                <Box sx={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                                    mt: 0.5, borderRadius: '14px',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                    bgcolor: isDark ? alpha('#1e293b', 0.98) : alpha('#ffffff', 0.99),
                                    backdropFilter: 'blur(20px)',
                                    boxShadow: isDark ? `0 12px 40px ${alpha('#000', 0.5)}` : `0 12px 40px ${alpha('#000', 0.12)}`,
                                    overflow: 'hidden', maxHeight: 280, overflowY: 'auto',
                                }}>
                                    {results.map((user, idx) => {
                                        const uRole = user.claims?.super_admin ? 'super_admin' : user.claims?.admin ? 'admin' : null;
                                        return (
                                            <Box key={user.uid} onClick={() => handleSelect(user)}
                                                sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                                    px: 2, py: 1.25, cursor: 'pointer',
                                                    borderBottom: idx < results.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.07)}` : 'none',
                                                    '&:hover': { bgcolor: alpha(RED, isDark ? 0.07 : 0.04) },
                                                    transition: 'background 0.15s',
                                                }}
                                            >
                                                <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: alpha(ACCENT, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                                    {user.photoURL
                                                        ? <Box component="img" src={user.photoURL} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        : <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: ACCENT }}>{(user.displayName || user.email || '?')[0].toUpperCase()}</Typography>
                                                    }
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                                            {user.displayName || <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>No name</Box>}
                                                        </Typography>
                                                        {uRole && (
                                                            <Chip label={uRole === 'super_admin' ? 'Super Admin' : 'Admin'} size="small"
                                                                sx={{ height: 16, borderRadius: '4px', fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha(uRole === 'super_admin' ? RED : AMBER, 0.15), color: uRole === 'super_admin' ? RED : AMBER }} />
                                                        )}
                                                        <Chip label={user.plan || 'free'} size="small"
                                                            sx={{ height: 16, borderRadius: '4px', fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha(PLAN_COLOR[user.plan] || ACCENT, 0.12), color: PLAN_COLOR[user.plan] || ACCENT }} />
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" noWrap>{user.email}</Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                            {query.length >= 2 && !searching && results.length === 0 && (
                                <Box sx={{ mt: 0.5, p: 2.5, borderRadius: '12px', textAlign: 'center', border: `1px dashed ${alpha(theme.palette.divider, 0.25)}` }}>
                                    <Typography variant="body2" color="text.secondary">No users found for "{query}"</Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                {/* ── Step 2: Set role (only when user selected) ── */}
                {selected && (
                    <>
                        {/* Role picker */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" sx={{
                                fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
                                textTransform: 'uppercase', color: ACCENT, display: 'block', mb: 1.5,
                            }}>
                                ② Admin Role
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {ROLE_OPTIONS.map(opt => {
                                    const active = role === opt.value;
                                    return (
                                        <Box key={opt.value} onClick={() => setRole(opt.value)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                p: 1.5, borderRadius: '12px', cursor: 'pointer',
                                                border: `1.5px solid ${active ? alpha(opt.color, 0.5) : alpha(theme.palette.divider, 0.2)}`,
                                                bgcolor: active ? alpha(opt.color, isDark ? 0.08 : 0.05) : 'transparent',
                                                transition: 'all 0.2s',
                                                '&:hover': { borderColor: alpha(opt.color, 0.4), bgcolor: alpha(opt.color, 0.04) },
                                            }}
                                        >
                                            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: alpha(opt.color, active ? 0.18 : 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                                                {React.cloneElement(opt.icon, { sx: { fontSize: 18, color: opt.color } })}
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: active ? opt.color : 'text.primary' }}>
                                                    {opt.label}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                                                    {opt.description}
                                                </Typography>
                                            </Box>
                                            {active && <CheckCircleIcon sx={{ color: opt.color, fontSize: 18, flexShrink: 0 }} />}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Plan assignment */}
                        <Box>
                            <Typography variant="caption" sx={{
                                fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
                                textTransform: 'uppercase', color: GREEN, display: 'block', mb: 1.5,
                            }}>
                                ③ Assign Plan (optional)
                            </Typography>
                            <FormControl size="small" fullWidth sx={fieldSx(GREEN)}>
                                <InputLabel>Plan</InputLabel>
                                <Select value={plan} label="Plan" onChange={e => setPlan(e.target.value)}>
                                    {Object.entries(plans).map(([id, p]) => (
                                        <MenuItem key={id} value={id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.accent || ACCENT, flexShrink: 0 }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name || id}</Typography>
                                                <Typography variant="caption" color="text.secondary">({id})</Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: 'block', fontSize: '0.7rem' }}>
                                Leave unchanged to keep the user's current plan. Changing the plan also updates Firebase custom claims.
                            </Typography>
                        </Box>
                    </>
                )}
            </DialogContent>

            {/* Footer */}
            <Box sx={{
                px: 3, py: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex', gap: 1.5, justifyContent: 'flex-end',
                bgcolor: isDark ? alpha('#000', 0.2) : alpha('#f8fafc', 0.8),
                borderRadius: '0 0 20px 20px',
            }}>
                <Button onClick={onClose} disabled={saving}
                    sx={{ borderRadius: '10px', color: 'text.secondary', border: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
                    Close
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || !selected || !role}
                    startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <VerifiedUserIcon />}
                    sx={{
                        borderRadius: '10px', fontWeight: 700, px: 2.5,
                        bgcolor: RED, '&:hover': { bgcolor: alpha(RED, 0.85) },
                        boxShadow: `0 4px 14px ${alpha(RED, 0.3)}`,
                    }}
                >
                    Apply Changes
                </Button>
            </Box>
        </Dialog>
    );
}

// ─── Plan card ─────────────────────────────────────────────────────────────────
// ─── Sortable wrapper for PlanCard ────────────────────────────────────────────
function SortablePlanCard({ planId, plan, onEdit, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: planId });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 9 : 'auto',
        position: 'relative',
    };
    return (
        <div ref={setNodeRef} style={style}>
            <PlanCard
                planId={planId}
                plan={plan}
                onEdit={onEdit}
                onDelete={onDelete}
                dragHandleProps={{ ...attributes, ...listeners }}
            />
        </div>
    );
}

function PlanCard({ planId, plan, onEdit, onDelete, dragHandleProps = {} }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isActive = plan.active !== false;
    const accent = plan.accent || '#6366f1';

    return (
        <Card sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px',
            border: `1px solid ${isActive ? alpha(accent, 0.35) : alpha(theme.palette.divider, 0.2)}`,
            bgcolor: isDark ? alpha('#0f172a', 0.7) : alpha('#ffffff', 0.8),
            opacity: isActive ? 1 : 0.55,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.2s',
            '&:hover': {
                boxShadow: isActive ? `0 8px 30px ${alpha(accent, 0.2)}` : 'none',
                transform: isActive ? 'translateY(-2px)' : 'none',
            },
        }}>
            {/* Top gradient accent bar */}
            <Box sx={{
                height: 3,
                background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.4)})`,
            }} />

            {/* Subtle glow blob */}
            <Box sx={{
                position: 'absolute', top: -20, right: -20,
                width: 80, height: 80, borderRadius: '50%',
                bgcolor: alpha(accent, isDark ? 0.08 : 0.05),
                filter: 'blur(20px)', pointerEvents: 'none',
            }} />

            <CardContent sx={{ flex: 1, pb: 0, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: accent, fontSize: '0.7rem', fontWeight: 600 }}>
                            {planId}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {plan.name || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {plan.target}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="Drag to reorder">
                            <Box
                                {...dragHandleProps}
                                sx={{
                                    cursor: 'grab',
                                    color: 'text.disabled',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 0.5,
                                    borderRadius: '6px',
                                    '&:hover': { color: accent, bgcolor: alpha(accent, 0.08) },
                                    '&:active': { cursor: 'grabbing' },
                                    touchAction: 'none',
                                }}
                            >
                                <DragIcon sx={{ fontSize: 18 }} />
                            </Box>
                        </Tooltip>
                        {plan.badge && (
                            <Chip label={plan.badge} size="small" sx={{
                                bgcolor: accent, color: '#fff', fontWeight: 700,
                                fontSize: '0.62rem', height: 20, borderRadius: '6px',
                            }} />
                        )}
                        {!isActive && (
                            <Chip label="INACTIVE" size="small" sx={{ fontSize: '0.62rem', height: 20, borderRadius: '6px' }} />
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 1.5, borderColor: alpha(theme.palette.divider, 0.15) }} />

                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled' }}>
                        Pricing
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.25 }}>
                        USD: <Box component="span" sx={{ fontWeight: 700, color: GREEN }}>{plan.price?.usd || '—'}</Box>{plan.period} &nbsp;·&nbsp;
                        INR: <Box component="span" sx={{ fontWeight: 700, color: GREEN }}>{plan.price?.inr || '—'}</Box>{plan.period}
                    </Typography>
                </Box>

                <Box sx={{ mb: 1.5, p: 1, bgcolor: alpha(AMBER, isDark ? 0.08 : 0.05), borderRadius: '8px', border: `1px solid ${alpha(AMBER, 0.15)}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: AMBER }}>
                        Monthly AI Quota
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.25 }}>
                        USD: <strong>{(plan.quota?.usd ?? '—').toLocaleString()}</strong> &nbsp;·&nbsp;
                        INR: <strong>{(plan.quota?.inr ?? '—').toLocaleString()}</strong>
                    </Typography>
                </Box>

                <Box sx={{ mb: 1.5, p: 1, bgcolor: alpha(GREEN, isDark ? 0.08 : 0.05), borderRadius: '8px', border: `1px solid ${alpha(GREEN, 0.15)}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: GREEN }}>
                        Daily Engagement Limit
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.25 }}>
                        USD: <strong>{(plan.engagement_daily?.usd ?? '—').toLocaleString()}</strong> &nbsp;·&nbsp;
                        INR: <strong>{(plan.engagement_daily?.inr ?? '—').toLocaleString()}</strong>
                    </Typography>
                </Box>

                <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled' }}>
                        Stripe IDs
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', color: plan.priceIds?.usd ? 'text.primary' : 'text.disabled', fontSize: '0.7rem', mt: 0.25 }}>
                        USD: {plan.priceIds?.usd || '(not set)'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', color: plan.priceIds?.inr ? 'text.primary' : 'text.disabled', fontSize: '0.7rem' }}>
                        INR: {plan.priceIds?.inr || '(not set)'}
                    </Typography>
                </Box>

                <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#3395ff' }}>
                        Razorpay
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', color: plan.razorpayPlanId ? 'text.primary' : 'error.main', fontSize: '0.7rem', mt: 0.25 }}>
                        Plan ID: {plan.razorpayPlanId || '(not set — INR payments will fail)'}
                    </Typography>
                </Box>

                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                    {plan.features_global?.length || 0} global · {plan.features_ind?.length || 0} India features
                </Typography>
            </CardContent>

            <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 1.5 }}>
                <Tooltip title="Edit plan">
                    <IconButton size="small" onClick={() => onEdit(planId, plan)}
                        sx={{ color: ACCENT, '&:hover': { bgcolor: alpha(ACCENT, 0.1) } }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
                {planId !== 'free' && (
                    <Tooltip title={isActive ? 'Deactivate plan' : 'Already inactive'}>
                        <span>
                            <IconButton size="small" onClick={() => onDelete(planId)} disabled={!isActive}
                                sx={{ color: RED, '&:hover': { bgcolor: alpha(RED, 0.1) } }}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </CardActions>
        </Card>
    );
}

// ─── Discounts / Coupons panel ────────────────────────────────────────────────
function DiscountsPanel({ onNotify, planIds }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [form, setForm] = useState({
        name: '', discount_type: 'percent', amount: '', duration: 'once',
        duration_months: '', applies_to_plans: [],
        start_date: '', expiry_date: '', max_redemptions: '',
    });

    const load = useCallback(async () => {
        setLoading(true);
        try { setCoupons(await adminApi.listDiscounts()); }
        catch (e) { onNotify('error', e.message || 'Failed to load coupons'); }
        finally { setLoading(false); }
    }, [onNotify]);

    useEffect(() => { load(); }, [load]);

    const handleCreate = async () => {
        setCreating(true);
        try {
            await adminApi.createDiscount({
                ...form,
                amount: Number(form.amount),
                duration_months: form.duration === 'repeating' ? Number(form.duration_months) : undefined,
                redeem_by: form.expiry_date ? Math.floor(new Date(form.expiry_date).getTime() / 1000) : undefined,
                max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : undefined,
                metadata: form.start_date ? { start_date: form.start_date } : undefined,
            });
            onNotify('success', 'Coupon created.');
            setForm({ name: '', discount_type: 'percent', amount: '', duration: 'once', duration_months: '', applies_to_plans: [], start_date: '', expiry_date: '', max_redemptions: '' });
            load();
        } catch (e) {
            onNotify('error', e.message || 'Failed to create coupon');
        } finally { setCreating(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(`Deactivate coupon "${id}"?`)) return;
        try { await adminApi.deleteDiscount(id); onNotify('success', 'Coupon deactivated.'); load(); }
        catch (e) { onNotify('error', e.message || 'Failed'); }
    };

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const filteredCoupons = coupons.filter(c => {
        const q = search.toLowerCase();
        return !q || c.id?.toLowerCase().includes(q) || c.name?.toLowerCase().includes(q);
    });

    // Live preview text
    const previewText = (() => {
        if (!form.amount) return null;
        const val = form.discount_type === 'percent'
            ? `${form.amount}% off`
            : `$${(Number(form.amount) / 100).toFixed(2)} off`;
        const dur = form.duration === 'once' ? 'once'
            : form.duration === 'forever' ? 'forever'
            : `${form.duration_months || '?'} months`;
        const plans = form.applies_to_plans.length
            ? form.applies_to_plans.join(', ')
            : 'all plans';
        const expiry = form.expiry_date
            ? ` · expires ${new Date(form.expiry_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`
            : '';
        const cap = form.max_redemptions ? ` · max ${form.max_redemptions} uses` : '';
        return `${val} · ${dur} · ${plans}${expiry}${cap}`;
    })();

    const fieldSx = (focusColor = ACCENT) => ({
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            '& fieldset': { borderColor: alpha(theme.palette.divider, 0.3) },
            '&:hover fieldset': { borderColor: alpha(focusColor, 0.5) },
            '&.Mui-focused fieldset': { borderColor: focusColor },
        },
        '& .MuiInputLabel-root.Mui-focused': { color: focusColor },
        '& .MuiFormHelperText-root': { fontSize: '0.7rem', mt: 0.5 },
    });

    return (
        <Box>
            <Card sx={{
                mb: 3, borderRadius: '20px',
                border: `1px solid ${alpha(ACCENT, 0.2)}`,
                bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.8),
                overflow: 'hidden',
                boxShadow: isDark ? `0 4px 24px ${alpha('#000', 0.3)}` : `0 4px 24px ${alpha('#000', 0.06)}`,
            }}>
                {/* Card header — clickable to toggle form */}
                <Box
                    onClick={() => setFormOpen(o => !o)}
                    sx={{
                        px: 3, py: 2.5,
                        borderBottom: formOpen ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        cursor: 'pointer',
                        userSelect: 'none',
                        background: isDark
                            ? `linear-gradient(135deg, ${alpha(ACCENT, 0.1)} 0%, transparent 60%)`
                            : `linear-gradient(135deg, ${alpha(ACCENT, 0.05)} 0%, transparent 60%)`,
                        '&:hover': { bgcolor: alpha(ACCENT, 0.04) },
                        transition: 'background 0.2s',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 34, height: 34, borderRadius: '10px',
                            bgcolor: alpha(ACCENT, 0.15),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <DiscountIcon sx={{ color: ACCENT, fontSize: 18 }} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                Create New Coupon
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Stripe coupon — applied at checkout automatically
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Live preview pill */}
                        {previewText && formOpen && (
                            <Chip
                                label={previewText}
                                size="small"
                                onClick={e => e.stopPropagation()}
                                sx={{
                                    bgcolor: alpha(GREEN, 0.12), color: GREEN,
                                    fontWeight: 600, fontSize: '0.72rem',
                                    borderRadius: '8px', height: 24,
                                    border: `1px solid ${alpha(GREEN, 0.25)}`,
                                }}
                            />
                        )}
                        <ExpandMoreIcon sx={{
                            color: 'text.secondary', fontSize: 20,
                            transform: formOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.25s',
                        }} />
                    </Box>
                </Box>

                <Collapse in={formOpen}>
                <Box sx={{ p: 3 }}>
                    {/* Row 1 — Identity */}
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{
                            fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: ACCENT, display: 'block', mb: 1.5,
                        }}>
                            Coupon Identity
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={8}>
                                <TextField size="small" fullWidth
                                    label="Coupon Name"
                                    value={form.name}
                                    onChange={e => set('name', e.target.value)}
                                    placeholder="e.g. Launch Week 20% Off"
                                    helperText="Internal label — customers see this on their invoice"
                                    sx={fieldSx(ACCENT)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl size="small" fullWidth sx={fieldSx(ACCENT)}>
                                    <InputLabel>Discount Type</InputLabel>
                                    <Select value={form.discount_type} label="Discount Type"
                                        onChange={e => set('discount_type', e.target.value)}>
                                        <MenuItem value="percent">
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Percent off</Typography>
                                                <Typography variant="caption" color="text.secondary">e.g. 20% off total</Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="amount_off">
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Fixed amount off</Typography>
                                                <Typography variant="caption" color="text.secondary">e.g. $5.00 off (in cents)</Typography>
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ my: 2.5, borderColor: alpha(theme.palette.divider, 0.12) }} />

                    {/* Row 2 — Value & Duration */}
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{
                            fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: GREEN, display: 'block', mb: 1.5,
                        }}>
                            Value & Duration
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField size="small" fullWidth
                                    label={form.discount_type === 'percent' ? 'Discount Percent' : 'Amount in Cents'}
                                    type="number"
                                    value={form.amount}
                                    onChange={e => set('amount', e.target.value)}
                                    helperText={
                                        form.discount_type === 'percent'
                                            ? 'Enter 20 for 20% off'
                                            : 'Enter 500 for $5.00 off'
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography sx={{ color: GREEN, fontWeight: 700, fontSize: '0.9rem' }}>
                                                    {form.discount_type === 'percent' ? '%' : '¢'}
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldSx(GREEN)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl size="small" fullWidth sx={fieldSx(GREEN)}>
                                    <InputLabel>How long it applies</InputLabel>
                                    <Select value={form.duration} label="How long it applies"
                                        onChange={e => set('duration', e.target.value)}>
                                        <MenuItem value="once">
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Once</Typography>
                                                <Typography variant="caption" color="text.secondary">Applied on the first invoice only</Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="repeating">
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Repeating</Typography>
                                                <Typography variant="caption" color="text.secondary">Applied for N months</Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="forever">
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Forever</Typography>
                                                <Typography variant="caption" color="text.secondary">Applied on every invoice</Typography>
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {form.duration === 'repeating' && (
                                <Grid item xs={12} sm={4}>
                                    <TextField size="small" fullWidth
                                        label="Number of Months"
                                        type="number"
                                        inputProps={{ min: 1 }}
                                        value={form.duration_months}
                                        onChange={e => set('duration_months', e.target.value)}
                                        helperText="How many billing cycles to apply"
                                        sx={fieldSx(GREEN)}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>

                    <Divider sx={{ my: 2.5, borderColor: alpha(theme.palette.divider, 0.12) }} />

                    {/* Row 3 — Scope */}
                    <Box sx={{ mb: 2.5 }}>
                        <Typography variant="caption" sx={{
                            fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: AMBER, display: 'block', mb: 1.5,
                        }}>
                            Plan Scope
                        </Typography>
                        <FormControl size="small" fullWidth sx={fieldSx(AMBER)}>
                            <InputLabel>Applies to which plans?</InputLabel>
                            <Select
                                multiple
                                value={form.applies_to_plans}
                                onChange={e => set('applies_to_plans', e.target.value)}
                                input={<OutlinedInput label="Applies to which plans?" />}
                                renderValue={selected =>
                                    selected.length === 0
                                        ? 'All plans'
                                        : selected.map(s => (
                                            <Chip key={s} label={s} size="small" sx={{ mr: 0.5, height: 18, borderRadius: '5px', fontSize: '0.7rem' }} />
                                        ))
                                }
                            >
                                {planIds.map(id => (
                                    <MenuItem key={id} value={id}>{id}</MenuItem>
                                ))}
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 0.5 }}>
                                Leave empty to apply to all plans. Select specific plans to restrict usage.
                            </Typography>
                        </FormControl>
                    </Box>

                    <Divider sx={{ my: 2.5, borderColor: alpha(theme.palette.divider, 0.12) }} />

                    {/* Row 4 — Limits & Expiry */}
                    <Box sx={{ mb: 2.5 }}>
                        <Typography variant="caption" sx={{
                            fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: '#06b6d4', display: 'block', mb: 1.5,
                        }}>
                            Limits &amp; Expiry
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.72rem', display: 'block', mb: 0.75 }}>
                                        Start Date
                                    </Typography>
                                    <Box
                                        component="input"
                                        type="date"
                                        value={form.start_date}
                                        onChange={e => set('start_date', e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                        sx={{
                                            width: '100%',
                                            height: 38,
                                            borderRadius: '10px',
                                            border: `1px solid ${alpha('#06b6d4', 0.3)}`,
                                            bgcolor: isDark ? alpha('#06b6d4', 0.05) : alpha('#06b6d4', 0.03),
                                            color: 'inherit',
                                            px: 1.5,
                                            fontSize: '0.85rem',
                                            outline: 'none',
                                            fontFamily: 'inherit',
                                            cursor: 'pointer',
                                            '&:focus': { borderColor: '#06b6d4', boxShadow: `0 0 0 2px ${alpha('#06b6d4', 0.15)}` },
                                            '&::-webkit-calendar-picker-indicator': { filter: isDark ? 'invert(1) opacity(0.5)' : 'opacity(0.5)', cursor: 'pointer' },
                                        }}
                                    />
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem', mt: 0.5, display: 'block' }}>
                                        Stored as metadata — coupon is valid from this date
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.72rem', display: 'block', mb: 0.75 }}>
                                        Expiry Date
                                    </Typography>
                                    <Box
                                        component="input"
                                        type="date"
                                        value={form.expiry_date}
                                        onChange={e => set('expiry_date', e.target.value)}
                                        min={form.start_date || new Date().toISOString().split('T')[0]}
                                        sx={{
                                            width: '100%',
                                            height: 38,
                                            borderRadius: '10px',
                                            border: `1px solid ${alpha(RED, 0.3)}`,
                                            bgcolor: form.expiry_date
                                                ? (isDark ? alpha(RED, 0.06) : alpha(RED, 0.04))
                                                : (isDark ? alpha(theme.palette.divider, 0.06) : alpha(theme.palette.divider, 0.04)),
                                            color: 'inherit',
                                            px: 1.5,
                                            fontSize: '0.85rem',
                                            outline: 'none',
                                            fontFamily: 'inherit',
                                            cursor: 'pointer',
                                            '&:focus': { borderColor: RED, boxShadow: `0 0 0 2px ${alpha(RED, 0.15)}` },
                                            '&::-webkit-calendar-picker-indicator': { filter: isDark ? 'invert(1) opacity(0.5)' : 'opacity(0.5)', cursor: 'pointer' },
                                        }}
                                    />
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem', mt: 0.5, display: 'block' }}>
                                        {form.expiry_date
                                            ? `Expires ${new Date(form.expiry_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}`
                                            : 'Leave blank for no expiry'}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    size="small" fullWidth
                                    label="Max Redemptions"
                                    type="number"
                                    inputProps={{ min: 1 }}
                                    value={form.max_redemptions}
                                    onChange={e => set('max_redemptions', e.target.value)}
                                    helperText="Leave blank for unlimited uses"
                                    placeholder="e.g. 100"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography sx={{ color: '#06b6d4', fontWeight: 700, fontSize: '0.85rem' }}>#</Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldSx('#06b6d4')}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Create button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" onClick={handleCreate}
                            disabled={creating || !form.name || !form.amount}
                            startIcon={creating ? <CircularProgress size={15} color="inherit" /> : <AddIcon />}
                            sx={{
                                borderRadius: '10px', px: 3, fontWeight: 700,
                                bgcolor: ACCENT, '&:hover': { bgcolor: alpha(ACCENT, 0.85) },
                                boxShadow: `0 4px 14px ${alpha(ACCENT, 0.35)}`,
                            }}>
                            Create Coupon
                        </Button>
                    </Box>
                </Box>
                </Collapse>
            </Card>

            {/* Existing coupons */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : coupons.length === 0 ? (
                <Box sx={{
                    textAlign: 'center', py: 5,
                    borderRadius: '16px',
                    border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
                }}>
                    <DiscountIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary" variant="body2">No active coupons yet.</Typography>
                    <Typography color="text.disabled" variant="caption">Create one above to get started.</Typography>
                </Box>
            ) : (
                <Card sx={{ borderRadius: '16px', border: `1px solid ${alpha(theme.palette.divider, 0.15)}`, overflow: 'hidden' }}>
                    <Box sx={{
                        px: 2, py: 1.5,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        bgcolor: alpha(theme.palette.divider, 0.04),
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                    }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            Active Coupons ({filteredCoupons.length}{search ? ` of ${coupons.length}` : ''})
                        </Typography>
                        {/* Search bar */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            borderRadius: '10px',
                            border: `1px solid ${search ? alpha(ACCENT, 0.4) : alpha(theme.palette.divider, 0.3)}`,
                            bgcolor: search ? alpha(ACCENT, isDark ? 0.06 : 0.04) : alpha(theme.palette.divider, 0.04),
                            px: 1.5, py: 0.5,
                            minWidth: 220,
                            transition: 'all 0.2s',
                        }}>
                            <SearchIcon sx={{ fontSize: 15, color: search ? ACCENT : 'text.disabled', flexShrink: 0 }} />
                            <Box
                                component="input"
                                placeholder="Search by name or ID…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                sx={{
                                    border: 'none', outline: 'none',
                                    bgcolor: 'transparent', color: 'inherit',
                                    fontSize: '0.8rem', fontFamily: 'inherit',
                                    flex: 1, minWidth: 0,
                                    '&::placeholder': { color: 'text.disabled', opacity: 0.7 },
                                }}
                            />
                            {search && (
                                <IconButton size="small" onClick={() => setSearch('')}
                                    sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: RED } }}>
                                    <ClearIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: alpha(theme.palette.divider, 0.03) }}>
                                {['Stripe ID', 'Name', 'Discount', 'Duration', 'Applies To', 'Expiry / Cap', ''].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase', py: 1.2, color: 'text.disabled' }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCoupons.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <SearchIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5, display: 'block', mx: 'auto' }} />
                                        <Typography variant="body2" color="text.secondary">No coupons match "{search}"</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : filteredCoupons.map(c => (
                                <TableRow key={c.id} sx={{ '&:hover': { bgcolor: alpha(theme.palette.divider, 0.04) } }}>
                                    <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', color: ACCENT, fontWeight: 600 }}>{c.id}</Typography></TableCell>
                                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{c.name}</Typography></TableCell>
                                    <TableCell>
                                        <Chip size="small" label={c.discount_type === 'percent' ? `${c.amount}% off` : `$${(c.amount / 100).toFixed(2)} off`}
                                            sx={{ bgcolor: alpha(GREEN, 0.12), color: GREEN, fontWeight: 700, height: 20, borderRadius: '6px', fontSize: '0.72rem' }} />
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                                                {c.duration}
                                            </Typography>
                                            {c.duration_months && (
                                                <Typography variant="caption" color="text.secondary">
                                                    for {c.duration_months} months
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {(c.applies_to_plans && c.applies_to_plans.length > 0)
                                            ? c.applies_to_plans.map(p => <Chip key={p} label={p} size="small" sx={{ mr: 0.5, height: 20, borderRadius: '6px', fontSize: '0.68rem', bgcolor: alpha(ACCENT, 0.1), color: ACCENT }} />)
                                            : <Chip label="All plans" size="small" sx={{ height: 20, borderRadius: '6px', fontSize: '0.68rem', bgcolor: alpha(theme.palette.divider, 0.15), color: 'text.secondary' }} />}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                            {c.redeem_by ? (
                                                <Chip
                                                    label={`Expires ${new Date(c.redeem_by * 1000).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                                                    size="small"
                                                    sx={{
                                                        height: 20, borderRadius: '6px', fontSize: '0.67rem',
                                                        bgcolor: new Date(c.redeem_by * 1000) < new Date()
                                                            ? alpha(RED, 0.12) : alpha('#06b6d4', 0.1),
                                                        color: new Date(c.redeem_by * 1000) < new Date()
                                                            ? RED : '#06b6d4',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>No expiry</Typography>
                                            )}
                                            {c.max_redemptions && (
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                    {c.times_redeemed ?? 0}/{c.max_redemptions} uses
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleDelete(c.id)}
                                            sx={{ color: RED, '&:hover': { bgcolor: alpha(RED, 0.1) } }}>
                                            <DeleteIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}
        </Box>
    );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function AdminPlans() {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [tab, setTab] = useState(0);
    const [plans, setPlans] = useState({});
    const [planOrder, setPlanOrder] = useState([]); // ordered array of plan IDs
    const [loading, setLoading] = useState(true);
    const [snack, setSnack] = useState({ open: false, type: 'success', msg: '' });
    const [seeding, setSeeding] = useState(false);
    const [editDialog, setEditDialog] = useState({ open: false, planId: null, data: null, isNew: false });
    const [quotaDialog, setQuotaDialog]       = useState(false);
    const [topupDialog, setTopupDialog]       = useState(false);
    const [manageUserDialog, setManageUserDialog] = useState(false);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const notify = useCallback((type, msg) => setSnack({ open: true, type, msg }), []);

    const loadPlans = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminApi.listPlans();
            setPlans(data || {});
            // Sort plan IDs by sort_order (fallback to insertion order)
            const sorted = Object.entries(data || {})
                .sort(([, a], [, b]) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
                .map(([id]) => id);
            setPlanOrder(sorted);
        } catch (err) {
            notify('error', err.message || 'Failed to load plans');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { loadPlans(); }, [loadPlans]);

    const handleEdit = (planId, plan) => setEditDialog({ open: true, planId, data: plan, isNew: false });
    const handleCreate = () => setEditDialog({ open: true, planId: null, data: null, isNew: true });
    const handleSaved = () => { notify('success', 'Plan saved successfully.'); loadPlans(); };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = planOrder.indexOf(active.id);
        const newIndex = planOrder.indexOf(over.id);
        const newOrder = arrayMove(planOrder, oldIndex, newIndex);
        setPlanOrder(newOrder); // optimistic update
        try {
            await adminApi.reorderPlans(newOrder);
        } catch (err) {
            notify('error', err.message || 'Failed to save order');
            setPlanOrder(planOrder); // revert on error
        }
    };

    const handleDelete = async (planId) => {
        if (!window.confirm(`Deactivate plan "${planId}"? Existing subscribers keep access.`)) return;
        try {
            await adminApi.deletePlan(planId);
            notify('success', `Plan "${planId}" deactivated.`);
            loadPlans();
        } catch (err) {
            notify('error', err.message || 'Deactivation failed');
        }
    };

    const handleSeed = async () => {
        if (!window.confirm('Seed default plan data into Firestore? Existing Stripe IDs are kept.')) return;
        setSeeding(true);
        try {
            const res = await adminApi.seedPlans();
            notify('success', res.message || 'Plans seeded.');
            loadPlans();
        } catch (err) {
            notify('error', err.message || 'Seed failed');
        } finally {
            setSeeding(false); }
    };

    const TABS = ['Plans', 'Discounts'];

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="overline" sx={{ color: GREEN, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.12em' }}>
                        Admin
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Plan Management</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Manage plans, quotas, Stripe IDs and discount coupons — live from Firestore.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={loadPlans} disabled={loading} size="small"
                            sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.3)}`, borderRadius: '10px' }}>
                            <RefreshIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Button variant="outlined" size="small" startIcon={<AdminIcon />}
                        onClick={() => setManageUserDialog(true)}
                        sx={{ borderRadius: '10px', borderColor: alpha(RED, 0.5), color: RED, '&:hover': { borderColor: RED, bgcolor: alpha(RED, 0.06) } }}>
                        Manage Users
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<PersonIcon />}
                        onClick={() => setQuotaDialog(true)}
                        sx={{ borderRadius: '10px', borderColor: alpha(AMBER, 0.5), color: AMBER, '&:hover': { borderColor: AMBER, bgcolor: alpha(AMBER, 0.06) } }}>
                        User Quota Override
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<QuotaIcon />}
                        onClick={() => setTopupDialog(true)}
                        sx={{ borderRadius: '10px', borderColor: alpha(GREEN, 0.5), color: GREEN, '&:hover': { borderColor: GREEN, bgcolor: alpha(GREEN, 0.06) } }}>
                        Engagement Top-Up
                    </Button>
                    <Button variant="outlined" size="small"
                        startIcon={seeding ? <CircularProgress size={13} color="inherit" /> : <SeedIcon />}
                        onClick={handleSeed} disabled={seeding}
                        sx={{ borderRadius: '10px', borderColor: alpha(theme.palette.divider, 0.4), color: 'text.secondary', '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: alpha(ACCENT, 0.06) } }}>
                        Seed Plans
                    </Button>
                    <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreate}
                        sx={{ borderRadius: '10px', bgcolor: GREEN, '&:hover': { bgcolor: alpha(GREEN, 0.85) }, boxShadow: `0 4px 14px ${alpha(GREEN, 0.3)}`, fontWeight: 700 }}>
                        New Plan
                    </Button>
                </Box>
            </Box>

            {/* Pill tab switcher */}
            <Box sx={{
                display: 'inline-flex', gap: 0.5, p: 0.5,
                borderRadius: '12px',
                bgcolor: isDark ? alpha('#000', 0.3) : alpha(theme.palette.divider, 0.08),
                border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                mb: 3,
            }}>
                {TABS.map((label, idx) => (
                    <Button key={label} size="small" onClick={() => setTab(idx)}
                        sx={{
                            borderRadius: '9px', px: 2.5, py: 0.6,
                            fontWeight: tab === idx ? 700 : 500,
                            fontSize: '0.82rem',
                            color: tab === idx ? '#fff' : 'text.secondary',
                            bgcolor: tab === idx ? ACCENT : 'transparent',
                            boxShadow: tab === idx ? `0 2px 8px ${alpha(ACCENT, 0.35)}` : 'none',
                            '&:hover': { bgcolor: tab === idx ? ACCENT : alpha(ACCENT, 0.08) },
                            transition: 'all 0.2s',
                        }}>
                        {idx === 1 && <DiscountIcon sx={{ fontSize: 14, mr: 0.5 }} />}
                        {label}
                    </Button>
                ))}
            </Box>

            {/* ── Plans tab ── */}
            {tab === 0 && (
                <>
                    <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
                        Changes are live within 5 minutes (plan_service cache TTL). Deactivating a plan does not revoke existing subscribers.
                    </Alert>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                    ) : Object.keys(plans).length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography color="text.secondary" sx={{ mb: 2 }}>
                                No plans found. Seed defaults or create a plan manually.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <Button variant="outlined" startIcon={<SeedIcon />} onClick={handleSeed} disabled={seeding}
                                    sx={{ borderRadius: '10px' }}>
                                    Seed Default Plans
                                </Button>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}
                                    sx={{ borderRadius: '10px', bgcolor: GREEN }}>
                                    Create Plan
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={planOrder} strategy={rectSortingStrategy}>
                                <Grid container spacing={2}>
                                    {planOrder.map((planId) => plans[planId] && (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={planId}>
                                            <SortablePlanCard
                                                planId={planId}
                                                plan={plans[planId]}
                                                onEdit={handleEdit}
                                                onDelete={handleDelete}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </SortableContext>
                        </DndContext>
                    )}
                </>
            )}

            {/* ── Discounts tab ── */}
            {tab === 1 && <DiscountsPanel onNotify={notify} planIds={planOrder} />}

            {/* Dialogs */}
            <PlanDialog
                open={editDialog.open}
                planId={editDialog.planId}
                initialData={editDialog.data}
                isNew={editDialog.isNew}
                onClose={() => setEditDialog(s => ({ ...s, open: false }))}
                onSaved={handleSaved}
            />
            <UserQuotaDialog open={quotaDialog} onClose={() => setQuotaDialog(false)} />
            <EngagementTopupDialog open={topupDialog} onClose={() => setTopupDialog(false)} />
            <ManageUserDialog
                open={manageUserDialog}
                onClose={() => setManageUserDialog(false)}
                plans={plans}
                onNotify={notify}
            />

            {/* Snackbar */}
            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snack.type} onClose={() => setSnack(s => ({ ...s, open: false }))}
                    sx={{ borderRadius: '12px', boxShadow: 4 }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
